/**
 * A aba "Consulta" do cliente (#1011): a consulta de cada oportunidade dele,
 * com a mesma tela do card da oportunidade (lista, "Nova consulta", "Editar").
 *
 * A consulta mora na oportunidade: o CRM guarda uma por oportunidade, e é por
 * ela que a carteira e o registro de acesso valem. Com mais de uma, a pessoa
 * escolhe de qual; sem nenhuma, a aba diz onde a consulta nasce, em vez de
 * oferecer um botão que não teria onde gravar.
 */

import { useState, type ReactNode } from 'react';
import type { OportunidadeDo360 } from '@cliqcrm/contracts';
import { AbaDaConsulta } from '@/widgets/detalhe-da-oportunidade';
import type { ContatoDoPaciente } from '@/features/saude/vincular-paciente';
import { CampoSelecao, Icone } from '@/shared/ui';
import './aba-da-consulta.css';

type ComId = OportunidadeDo360 & { readonly id: string };

export function ConsultaNoCliente({
  oportunidades,
  podeEditar,
  contato = null,
}: {
  /** As oportunidades do cliente no 360. Só as que já têm id canônico abrem a consulta. */
  oportunidades: readonly OportunidadeDo360[];
  podeEditar: boolean;
  /** O contato do cliente: a busca do paciente usa o telefone dele. */
  contato?: ContatoDoPaciente | null;
}): ReactNode {
  const comId = oportunidades.filter((o): o is ComId => o.id !== null);
  const [escolhida, definirEscolhida] = useState(comId[0]?.id ?? '');
  const atual = comId.find((o) => o.id === escolhida) ?? comId[0];

  if (!atual) {
    return (
      <div className="consulta-cli__vazio">
        <Icone nome="calendario" />
        <p className="consulta-cli__texto">Este cliente ainda não tem oportunidade.</p>
        <p className="consulta-cli__apoio">
          A consulta é marcada dentro de uma oportunidade. Crie uma no Funil e volte aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="consulta-cli">
      {comId.length > 1 && (
        <div className="consulta-cli__escolha">
          <CampoSelecao
            id="consulta-cli-oportunidade"
            rotulo="Oportunidade"
            value={atual.id}
            onChange={(e) => definirEscolhida(e.currentTarget.value)}
          >
            {comId.map((o) => (
              <option key={o.id} value={o.id}>
                {o.titulo ?? 'Oportunidade sem título'}
              </option>
            ))}
          </CampoSelecao>
        </div>
      )}
      {/* A chave recomeça a aba ao trocar de oportunidade: nada da outra fica na tela. */}
      <AbaDaConsulta
        key={atual.id}
        oportunidadeId={atual.id}
        podeEditar={podeEditar}
        contato={contato}
      />
    </div>
  );
}
